import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertService } from '../../services/alert.service';
import InvalidFeedback from './invalid-feedback.component';
import Picker from '@emoji-mart/react';
import { useDarkMode } from '../../hooks/use-dark-mode.hook';

const MessageRCSvalidate = React.forwardRef(
  (
    {
      label,
      value,
      payload = '',
      counter,
      errors,
      requiredIndicator = '',
      inputName = '',
      optional = false,
      maxLength,
      setValue,
      disableEmojis = false,
      ...register
    }: any,
    ref
  ) => {
    const { t } = useTranslation();
    const [count, setCount] = useState(0);
    const isDarkTheme = useDarkMode();
    const [showEmojis, setShowEmojis] = useState(false);
    const [textValue, setTextValue] = useState(value || '');
    const [error, setError] = useState<any>(null);

    const emojiPickerRef = useRef<HTMLDivElement | null>(null);
    const buttonRef = useRef<HTMLButtonElement | null>(null);

    const countCharacters = (text: string) => {
      const encoder = new TextEncoder();
      const encoded = encoder.encode(text);
      return encoded.length;
    };

    const trimEmojis = (text: string, maxLength: number) => {
      let finalText = text;
      while (countCharacters(finalText) > maxLength) {
        finalText = finalText.slice(0, finalText.length - 1);
      }
      return finalText;
    };

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      let newValue = event.target.value.replace(/^[\s]+/, '');

      newValue = trimEmojis(newValue, maxLength);
      setTextValue(newValue);
      setValue(inputName, newValue);

      if (register.onChange) {
        register.onChange(event);
      }
      const finalCount = countCharacters(newValue);
      if (finalCount > (counter || maxLength)) {
        setError({
          type: 'maxLength',
          message: `${counter || maxLength}`
        });
      } else {
        setError(null);
      }
    };

    const handleEmojiSelect = (emojiObject: any) => {
      const emoji = emojiObject.native;
      const currentMessage = textValue || '';
      const updatedMessage = currentMessage + emoji;
      const updatedCount = countCharacters(updatedMessage);

      if (updatedCount > (counter || maxLength)) {
        AlertService.toastInfo(
          `¡Atención! El límite de caracteres es ${counter || maxLength}. Si agregas este emoji ${emoji}, el total sería ${updatedCount}. Verifica o elige otro emoji.`
        );
        return;
      }

      setTextValue(updatedMessage);
      setValue(inputName, updatedMessage, { shouldValidate: true });
    };

    const handlePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const pastedText = event.clipboardData.getData('text');
      const newText = textValue + pastedText;
      const trimmedText = trimEmojis(newText, maxLength);

      if (trimmedText !== newText) {
        event.preventDefault();
        setTextValue(trimmedText);
        setValue(inputName, trimmedText);
      }

      const pastedCount = countCharacters(trimmedText);
      if (pastedCount > (counter || maxLength)) {
        setError({
          type: 'maxLength',
          message: `${counter || maxLength}`,
          ref: event.target
        });
      } else {
        setError(null);
      }
    };

    useEffect(() => {
      const handleClickOutside = (event: any) => {
        if (emojiPickerRef.current && emojiPickerRef.current.contains(event.target)) {
          return;
        }
        if (buttonRef.current && buttonRef.current.contains(event.target)) {
          return;
        }
        setShowEmojis(false);
      };
      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }, []);

    useEffect(() => {
      const count = countCharacters(textValue);
      setCount(count);
    }, [textValue]);

    useEffect(() => {
      setTextValue(payload.message || value);
    }, []);
    return (
      <>
        {label && (
          <>
            <div className="form-group d-flex justify-content-between align-items-center">
              <div className="d-flex flex-column mb-3">
                <span className={`fw-bolder fs-3 ${requiredIndicator}`}>
                  {label}{' '}
                  {optional && (
                    <span className="fs-8 text-muted ms-1">({t('general.optional')})</span>
                  )}
                </span>
              </div>
              <div className="d-flex align-items-center">
                <div className="form-text mt-1 text-end">
                  {count}/{counter}
                </div>
              </div>
            </div>
          </>
        )}
        <div className="position-relative">
          <textarea
            ref={ref}
            className={`form-control form-control-lg form-control-solid ${errors || error ? 'is-invalid' : ''}`}
            {...register}
            rows={5}
            value={textValue}
            maxLength={maxLength}
            onChange={handleChange}
            onPaste={handlePaste}
          />
          {!disableEmojis && (
            <>
              <button
                type="button"
                className={`btn btn-sm btn-icon btn-active-light-primary position-absolute bottom-0 start-0 ms-2 ${errors || error ? 'mb-8' : 'mb-2'}`}
                ref={buttonRef}
                onClick={() => setShowEmojis((prev: any) => !prev)}
                disabled={count >= (counter || maxLength)}
              >
                <span className="fs-1">😀</span>
              </button>
              {showEmojis && (
                <div
                  className="dropdown-menu show form-group"
                  style={{ position: 'absolute', bottom: '9%', left: '7%' }}
                  ref={emojiPickerRef}
                >
                  <Picker
                    onEmojiSelect={handleEmojiSelect}
                    locale={t('languages.emoji-lang')}
                    title="Selecciona tu emoji"
                    emoji="point_up"
                    theme={isDarkTheme ? 'dark' : 'light'}
                  />
                </div>
              )}
            </>
          )}
          <InvalidFeedback error={error || errors} />
        </div>
      </>
    );
  }
);

export default MessageRCSvalidate;
